import {Flag, Separator, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Spinner, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';

import type {PriceBasis} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {precisionCalculation} from 'shared/web';

import {BasisPrice} from '../BasisPrice/BasisPrice';
import {MoneyText} from '../MoneyText/MoneyText';

export type PriceBreakdownProps = {
  currency: string;
  lines?: {id: string; label: string; amountNet: string; amountGross?: string; hint?: string}[];
  basis?: PriceBasis;
  totals?: {net: string; vat: string; gross: string; bundleSavingsNet?: string};
  proposed?: {net?: string; gross?: string; discountLabel?: string};
  cost?: {costNet?: string; totalCostNet?: string};
  canViewCost?: boolean;
  isPending: boolean;
  vatRateLabel?: string;
  variant?: 'card' | 'inline';
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

function MoneyRow(props: {
  label: string;
  amount?: string;
  currency: string;
  alternative?: boolean;
  color?: 'primary' | 'secondary' | 'success';
}) {
  if (!isNotNil(props.amount)) {
    return null;
  }
  return (
    <HStack justify="space-between" spacing={4}>
      <Text size="small" color={props.color ?? 'secondary'}>
        {props.label}
      </Text>
      <MoneyText
        amount={props.amount}
        currency={props.currency}
        size="small"
        alternative={props.alternative}
        color={props.color}
        align="right"
        noWrap
      />
    </HStack>
  );
}

type TotalsProps = Pick<PriceBreakdownProps, 'currency' | 'isPending' | 'vatRateLabel'> & {
  totals: NonNullable<PriceBreakdownProps['totals']>;
};

function BasisTotals(props: TotalsProps & {basis: PriceBasis}) {
  return (
    <>
      <HStack justify="space-between" align="center" spacing={4}>
        <VStack spacing={1}>
          <Text size="small" color="secondary">
            {t('general.labels.total', 'Total')}
          </Text>
          <Show when={props.isPending}>
            <Text size="xSmall" color="secondary">
              {t('entity.newVehicle.labels.updatingPrice', 'Updating price…')}
            </Text>
          </Show>
        </VStack>
        <HStack spacing={2} align="center">
          <Show when={props.isPending}>
            <Spinner size="small" />
          </Show>
          <BasisPrice
            net={props.totals.net}
            gross={props.totals.gross}
            basis={props.basis}
            currency={props.currency}
            size="large"
            alternative
            align="right"
            noWrap
          />
        </HStack>
      </HStack>
      <MoneyRow
        label={props.vatRateLabel ?? t('general.labels.vat', 'VAT')}
        amount={props.totals.vat}
        currency={props.currency}
      />
      <Show when={props.totals.bundleSavingsNet && Number(props.totals.bundleSavingsNet) !== 0}>
        <MoneyRow
          label={
            props.basis === 'gross'
              ? t('entity.newVehicle.labels.bundleSavingsNet', 'Bundle savings (net)')
              : t('entity.newVehicle.labels.bundleSavings', 'Bundle savings')
          }
          amount={props.totals.bundleSavingsNet}
          currency={props.currency}
          color="success"
        />
      </Show>
    </>
  );
}

function LegacyTotals(props: TotalsProps) {
  return (
    <>
      <MoneyRow
        label={t('entity.newVehicle.labels.net', 'Net')}
        amount={props.totals.net}
        currency={props.currency}
      />
      <MoneyRow
        label={props.vatRateLabel ?? t('general.labels.vat', 'VAT')}
        amount={props.totals.vat}
        currency={props.currency}
      />
      <Show when={props.totals.bundleSavingsNet && Number(props.totals.bundleSavingsNet) !== 0}>
        <MoneyRow
          label={t('entity.newVehicle.labels.bundleSavings', 'Bundle savings')}
          amount={props.totals.bundleSavingsNet}
          currency={props.currency}
          color="success"
        />
      </Show>
      <HStack justify="space-between" align="center" spacing={4}>
        <VStack spacing={1}>
          <Text size="small" color="secondary">
            {t('entity.newVehicle.labels.grossToCustomer', 'Gross to customer')}
          </Text>
          {/* Pending swaps this caption and adds a spinner in place, so repricing never
              changes the panel's height and shifts the layout on every selection. */}
          <Text size="xSmall" color="secondary">
            {props.isPending
              ? t('entity.newVehicle.labels.updatingPrice', 'Updating price…')
              : t('entity.newVehicle.labels.asConfiguredInclVat', 'As configured · incl. VAT')}
          </Text>
        </VStack>
        <HStack spacing={2} align="center">
          <Show when={props.isPending}>
            <Spinner size="small" />
          </Show>
          <MoneyText
            amount={props.totals.gross}
            currency={props.currency}
            size="large"
            alternative
            align="right"
            noWrap
          />
        </HStack>
      </HStack>
    </>
  );
}

/** Margin is what the dealer keeps: what the customer pays net, minus what the car costs us. */
function useDealerMargin(props: PriceBreakdownProps) {
  const formatNumber = useFormatNumber();
  const revenueNet = Number(props.proposed?.net ?? props.totals?.net);
  const costNet = Number(props.cost?.totalCostNet ?? props.cost?.costNet);

  if (!Number.isFinite(revenueNet) || !Number.isFinite(costNet)) {
    return null;
  }

  const amount = precisionCalculation.subtract(revenueNet, costNet);
  const basis = isNotNil(props.proposed?.net)
    ? t('entity.newVehicle.labels.marginOfProposed', 'of proposed selling price')
    : t('entity.newVehicle.labels.marginOfNet', 'of net price');

  return {
    amount: amount.toFixed(2),
    isNegative: amount < 0,
    caption:
      revenueNet > 0
        ? // Round the percentage itself: rounding the ratio and scaling by 100 afterwards reintroduces
          // float noise (0.101 * 100 = 10.100000000000001).
          `${formatNumber(precisionCalculation.round(precisionCalculation.divide(amount * 100, revenueNet), 1), 1)} % ${basis}`
        : basis,
  };
}

export function PriceBreakdown(props: PriceBreakdownProps) {
  const margin = useDealerMargin(props);

  if (props.isPending && !props.totals) {
    return (
      <HStack spacing={2} align="center" data-testid="new-vehicle-price-breakdown-pending">
        <Spinner size="small" />
        <Text size="small" color="secondary">
          {t('entity.newVehicle.labels.updatingPrice', 'Updating price…')}
        </Text>
      </HStack>
    );
  }

  if (!props.totals && !props.lines?.length) {
    return (
      <Text color="secondary">
        {t(
          'entity.newVehicle.priceBreakdown.empty',
          'Select a trim level and engine to see the price'
        )}
      </Text>
    );
  }

  if (!props.totals) {
    return (
      <VStack spacing={2} data-testid="new-vehicle-price-breakdown">
        {props.lines?.map((line) => (
          <VStack key={line.id} spacing={1}>
            <MoneyRow
              label={line.label}
              amount={
                props.basis === 'gross' ? (line.amountGross ?? line.amountNet) : line.amountNet
              }
              currency={props.currency}
            />
            <Show when={line.hint}>
              <Text size="xSmall" color="secondary">
                {line.hint}
              </Text>
            </Show>
          </VStack>
        ))}
      </VStack>
    );
  }

  const hasProposed = isNotNil(props.proposed?.net) || isNotNil(props.proposed?.gross);

  return (
    <VStack spacing={3} data-testid="new-vehicle-price-breakdown">
      <Show when={props.lines?.length}>
        <VStack spacing={2}>
          {props.lines?.map((line) => (
            <VStack key={line.id} spacing={1}>
              <MoneyRow
                label={line.label}
                amount={
                  props.basis === 'gross' ? (line.amountGross ?? line.amountNet) : line.amountNet
                }
                currency={props.currency}
              />
              <Show when={line.hint}>
                <Text size="xSmall" color="secondary">
                  {line.hint}
                </Text>
              </Show>
            </VStack>
          ))}
        </VStack>
        {/* Divides the line items from the totals — without items there is nothing to divide,
            and the consumer (summary rail) already draws its own rule above us. */}
        <Separator />
      </Show>
      {isNotNil(props.basis) ? (
        <BasisTotals {...props} totals={props.totals} basis={props.basis} />
      ) : (
        <LegacyTotals {...props} totals={props.totals} />
      )}
      <Show when={hasProposed}>
        <Separator />
        <VStack spacing={2}>
          <Heading size={4}>
            {t('entity.newVehicle.labels.proposedSellingPrice', 'Proposed selling price')}
          </Heading>
          <Show when={props.proposed?.discountLabel}>
            <Text size="xSmall" color="secondary">
              {props.proposed?.discountLabel}
            </Text>
          </Show>
          <MoneyRow
            label={t('entity.newVehicle.labels.net', 'Net')}
            amount={props.proposed?.net}
            currency={props.currency}
          />
          <MoneyRow
            label={t('entity.newVehicle.labels.gross', 'Gross')}
            amount={props.proposed?.gross}
            currency={props.currency}
            alternative
          />
        </VStack>
      </Show>
      <Show when={props.canViewCost && props.cost}>
        <Box backgroundColor="palettes.neutral.10.100" borderRadius="small" padding={3}>
          <VStack spacing={3}>
            <HStack spacing={2} align="center">
              <Flag
                size="small"
                isSubtle
                colorScheme="neutral"
                label={t('entity.newVehicle.labels.dealerOnly', 'Dealer only')}
              />
              <Tooltip
                placement="top"
                label={t(
                  'entity.newVehicle.labels.dealerOnlyTooltip',
                  'Internal figures: what this car costs the dealership and what is left after the customer price. Never share with the customer — only users with the cost permission see this block.'
                )}
              >
                <Icon value="action/info_outline" size={4} color="text.secondary" />
              </Tooltip>
            </HStack>
            <Show when={isNotNil(margin)}>
              <HStack justify="space-between" align="center" spacing={4}>
                <VStack spacing={1}>
                  <Text size="small" color="secondary">
                    {t('entity.newVehicle.labels.margin', 'Margin')}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {margin?.caption}
                  </Text>
                </VStack>
                <MoneyText
                  amount={margin?.amount ?? '0.00'}
                  currency={props.currency}
                  size="small"
                  alternative
                  color={margin?.isNegative ? 'danger' : undefined}
                  align="right"
                  noWrap
                />
              </HStack>
              <Separator spacing={0} />
            </Show>
            <VStack spacing={2}>
              <MoneyRow
                label={t('entity.newVehicle.labels.costBasis', 'Cost basis')}
                amount={props.cost?.costNet}
                currency={props.currency}
              />
              <MoneyRow
                label={t('entity.newVehicle.labels.totalCost', 'Total cost')}
                amount={props.cost?.totalCostNet}
                currency={props.currency}
              />
            </VStack>
          </VStack>
        </Box>
      </Show>
    </VStack>
  );
}
